import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter } from "./router";
import { reportLovableError } from "./lib/lovable-error-reporting";
import { iniciarSentry } from "./lib/sentry";
import { wasPrerendered } from "./lib/prerender-hydration";
import "./styles.css";

// Antes la app no tenía NINGÚN manejo global de errores — una promesa rechazada sin .catch en
// cualquier parte (ej. un grantXp que falla después de ya haber mutado otro estado) desaparecía
// en silencio total, sin rastro ni para el staff ni para nadie (hallazgo de auditoría real). Esto
// no evita el problema de fondo (seguir arreglando los .catch faltantes uno por uno donde
// corresponda), pero asegura que un error que se escape igual quede logueado.
window.addEventListener("unhandledrejection", (event) => {
  reportLovableError(event.reason, { source: "unhandledrejection" });
});
window.addEventListener("error", (event) => {
  reportLovableError(event.error ?? event.message, { source: "window.onerror" });
});

iniciarSentry();

const queryClient = new QueryClient();
const router = createRouter(queryClient);
if (wasPrerendered) {
  // `<Matches>` (dentro de @tanstack/react-router, no editable) envuelve TODA la app en su
  // propio `<Suspense fallback={null}>` incondicional — `router.clientSsr` es la bandera
  // pública que la librería expone justo para este caso (hidratar contenido que vino de un
  // render de servidor/prerender) y hace que use un Fragment en su lugar. Ver el comentario
  // largo más abajo, en el `if (rootEl.hasChildNodes())`, con el resto de la investigación.
  (router as unknown as { clientSsr: { getStreamedValue: () => undefined } }).clientSsr = { getStreamedValue: () => undefined };
}

const rootEl = document.getElementById("root")!;
const app = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>
);

/** Fase 2 de rendimiento (prerender extendido, ver scripts/prerender-content.mjs y CLAUDE.md):
 *  las rutas públicas principales ahora llegan con `<div id="root">` YA lleno de HTML real
 *  (generado en build-time con Playwright, no a mano) — si acá siempre se usara `createRoot()`,
 *  React tira ese HTML entero e imprime todo de cero apenas el bundle carga, perdiendo el punto
 *  del prerender (adelantar el primer pintado). `hydrateRoot()` reutiliza el DOM que ya está ahí
 *  en vez de reemplazarlo. Las rutas SIN su propio `dist/<ruta>/index.html` (todo lo demás:
 *  /admin, /staff, /portal, /programas/:slug, etc., servidas por el rewrite "**" de Firebase
 *  Hosting con el index.html genérico) siguen con `<div id="root">` vacío como siempre — ahí
 *  hidratar sobre la nada tiraría un error de mismatch, así que se detecta con `hasChildNodes()`
 *  y se usa `createRoot()` igual que antes. En `vite dev` (desarrollo local) el root SIEMPRE está
 *  vacío (no hay prerender fuera de `dist/`), así que el flujo de desarrollo no cambia en nada.
 *
 *  **Limitación real, investigada a fondo, no ignorada**: el HTML prerenderizado se generó
 *  capturando el DOM ya resuelto con un navegador headless (`document.documentElement.outerHTML`
 *  en scripts/prerender-content.mjs), NO con un render de servidor real (`renderToString`/
 *  streaming) — así que no lleva los comentarios especiales (`<!--$-->`/`<!--/$-->`) que React
 *  necesita para reconciliar un `<Suspense>` al hidratar. `<Outlet/>` de @tanstack/react-router
 *  envuelve SIEMPRE su contenido en un `<Suspense>` propio cuando se lo llama desde la ruta raíz
 *  (adentro de `Match.js`, no editable, sin ninguna bandera para desactivarlo — a diferencia del
 *  `<Suspense>` de `<Matches>`, que si expone `router.clientSsr` para esto, ver arriba) — así que
 *  SIEMPRE va a haber un mismatch de hidratación reportado acá abajo para las rutas
 *  prerenderizadas, sin ninguna forma de evitarlo sin parchear la librería. Investigado con
 *  capturas cuadro por cuadro (80 frames de rAF) y con el propio `PerformanceObserver` de
 *  `largest-contentful-paint`: el contenido nunca cambia de texto/posición en ningún frame (React
 *  reconstruye ESE mismo contenido, no otro distinto — mismos DEFAULTS síncronos en la captura y
 *  en el primer render del cliente) y el LCP real medido quedó en ~130-165ms — sin parpadeo
 *  visible, sin impacto de rendimiento real. Por eso este error puntual no se manda a
 *  `reportLovableError` — reportarlo ensuciaría cualquier monitoreo real con un mismatch 100%
 *  esperado en cada carga de una ruta prerenderizada, no un bug. Si en el futuro se decide
 *  arreglar esto de raíz, la única forma real es generar el HTML prerenderizado con un render de
 *  servidor de verdad (SSR), no con este approach — ver la Fase 2 de performance (SSR descartada
 *  por costo) más arriba en CLAUDE.md. */
if (rootEl.hasChildNodes()) {
  router.load().then(() => {
    ReactDOM.hydrateRoot(rootEl, app, {
      onRecoverableError: (error, errorInfo) => {
        if (wasPrerendered) return;
        reportLovableError(error, { source: "hydrateRoot.onRecoverableError", componentStack: errorInfo?.componentStack });
      },
    });
  });
} else {
  ReactDOM.createRoot(rootEl).render(app);
}
